import React, { ReactNode, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PubPackage,
  createPubPackage,
  updatePubPackage,
  deletePubPackage,
  movePubPackageUp,
  movePubPackageDown,
} from "../firebase/pubPackageService";
import { Timestamp } from "firebase/firestore";
import { globalRateLimiter } from "../utils/rateLimiter";
import { pubPackagesQuery, selectFeatured } from "../queries/contentQueries";

interface PubPackageContextType {
  pubPackages: PubPackage[];
  featuredPubPackages: PubPackage[];
  loading: boolean;
  error: Error | null;
  refreshPubPackages: () => Promise<void>;
  addPubPackage: (
    pubPackage: Omit<PubPackage, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editPubPackage: (
    id: string,
    pubPackage: Partial<Omit<PubPackage, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removePubPackage: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
}

const EMPTY: PubPackage[] = [];

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const PubPackageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;

export const usePubPackages = (): PubPackageContextType => {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery(pubPackagesQuery);
  const pubPackages = data ?? EMPTY;
  const featuredPubPackages = useMemo(() => selectFeatured(pubPackages), [pubPackages]);

  const setPubPackages = (update: (prev: PubPackage[]) => PubPackage[]) =>
    queryClient.setQueryData<PubPackage[]>(pubPackagesQuery.queryKey, (prev) =>
      update(prev ?? [])
    );

  const refreshPubPackages = async () => {
    await refetch();
  };

  const addPubPackage = async (
    pubPackage: Omit<PubPackage, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limiting for pub package operations
    if (!globalRateLimiter.isAllowed("add-pubpackage")) {
      throw new Error(
        "Too many requests. Please wait a moment before trying again."
      );
    }

    const id = await createPubPackage(pubPackage);
    setPubPackages((prev) => [
      ...prev,
      { ...pubPackage, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);
    return id;
  };

  const editPubPackage = async (
    id: string,
    pubPackage: Partial<Omit<PubPackage, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for pub package updates
    if (!globalRateLimiter.isAllowed(`edit-pubpackage-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updatePubPackage(id, pubPackage);
    setPubPackages((prev) =>
      prev.map((p) =>
        p.id === id
          ? ({ ...p, ...pubPackage, updatedAt: Timestamp.now() } as PubPackage)
          : p
      )
    );
  };

  const removePubPackage = async (id: string): Promise<void> => {
    await deletePubPackage(id);
    setPubPackages((prev) => prev.filter((p) => p.id !== id));
  };

  // Move operations change ordering server-side, so refetch
  const moveUp = async (id: string): Promise<void> => {
    await movePubPackageUp(id);
    await refreshPubPackages();
  };

  const moveDown = async (id: string): Promise<void> => {
    await movePubPackageDown(id);
    await refreshPubPackages();
  };

  return {
    pubPackages,
    featuredPubPackages,
    loading: isPending,
    error: error as Error | null,
    refreshPubPackages,
    addPubPackage,
    editPubPackage,
    removePubPackage,
    moveUp,
    moveDown,
  };
};
